import { cn } from "@tcg/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCan, useRegister } from "@/app/context";
import { Modal } from "@/components/Modal";
import { NumPad } from "@/components/NumPad";
import type { CartLine, DiscountAdd } from "@/lib/types";

import { localized } from "./labels";

const REASONS = ["reason_damage", "reason_bundle", "reason_regular", "reason_event"] as const;

export interface DiscountModalProps {
  open: boolean;
  /** A line discount; null = the whole receipt. */
  line: CartLine | null;
  onClose: () => void;
  /** Sends the discount (asks the manager when needed); resolves true when it was applied. */
  onApply: (body: DiscountAdd) => Promise<boolean>;
}

/**
 * 値引き (design/05 §3.1): % or yen + a reason from the list (or typed). Above the shop's threshold
 * (10 %) a cashier without the permission is told in one sentence that the manager must approve —
 * the manager's PIN dialog opens when it is sent; cancelling there changes nothing.
 */
export function DiscountModal({ open, line, onClose, onApply }: DiscountModalProps) {
  const { t, i18n } = useTranslation();
  const register = useRegister();
  const mayDiscountMore = useCan("pos.discount_over_10");
  const threshold = register.data?.rules.discountManagerThresholdPercent ?? 10;
  const [type, setType] = useState<"percent" | "amount">("percent");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState<string | null>(null);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const digits = Number.parseInt(value || "0", 10);
  const reasonText =
    reason === "reason_other" ? custom.trim() : reason ? t(`discount.${reason}`) : "";
  const valid = digits > 0 && (type === "amount" || digits <= 100) && reasonText.length > 0;
  const needsManager = type === "percent" && digits > threshold && !mayDiscountMore;

  const reset = () => {
    setValue("");
    setReason(null);
    setCustom("");
    setType("percent");
  };

  const apply = async () => {
    if (!valid || busy) {
      return;
    }
    setBusy(true);
    const ok = await onApply({
      scope: line ? "line" : "cart",
      ...(line ? { lineId: line.id } : {}),
      type,
      value: digits,
      reason: reasonText,
    });
    setBusy(false);
    if (ok) {
      reset();
      onClose();
    }
  };

  const title = line
    ? `${t("discount.title")} — ${localized(line.title, i18n.resolvedLanguage ?? "ja")}`
    : `${t("discount.title")} — ${t("discount.scope_cart")}`;
  return (
    <Modal
      open={open}
      title={title}
      onClose={() => {
        reset();
        onClose();
      }}
      busy={busy}
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-h-pos-touch rounded-md border px-6 text-lg"
          >
            {t("app.cancel")}
          </button>
          <button
            type="button"
            onClick={() => void apply()}
            disabled={!valid || busy}
            className="min-h-pos-touch rounded-md bg-primary px-8 text-lg font-semibold text-primary-foreground disabled:opacity-50"
          >
            {busy ? t("app.saving") : t("discount.apply")}
          </button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-3">
          <div
            role="radiogroup"
            aria-label={t("discount.value")}
            className="grid grid-cols-2 gap-2"
          >
            {(["percent", "amount"] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={type === kind}
                onClick={() => setType(kind)}
                className={cn(
                  "min-h-pos-touch rounded-md border text-xl",
                  type === kind && "border-primary bg-primary text-primary-foreground",
                )}
              >
                {t(kind === "percent" ? "discount.type_percent" : "discount.type_amount")}
              </button>
            ))}
          </div>
          <NumPad
            label={t("discount.value")}
            value={value}
            onChange={setValue}
            maxLength={type === "percent" ? 3 : 9}
            money={type === "amount"}
          />
          {needsManager ? (
            <p
              role="note"
              className="rounded-md border border-status-pending bg-status-pending/15 p-3"
            >
              {t("discount.needs_manager", { percent: threshold })}
            </p>
          ) : null}
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 font-medium">{t("discount.reason")}</legend>
          {[...REASONS, "reason_other" as const].map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={reason === key}
              onClick={() => setReason(key)}
              className={cn(
                "min-h-pos-touch rounded-md border px-4 text-left text-lg",
                reason === key && "border-primary bg-accent",
              )}
            >
              {t(`discount.${key}`)}
            </button>
          ))}
          {reason === "reason_other" ? (
            <input
              autoFocus
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              maxLength={200}
              aria-label={t("discount.reason_custom")}
              placeholder={t("discount.reason_custom")}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          ) : null}
        </fieldset>
      </div>
    </Modal>
  );
}
